import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { supabase } from '@/lib/supabase'; // Ajuste o caminho se necessário
import { MissionRow, MissionRequiredActions, MissionActionRequirement, Mission } from '@/lib/gamification/types'; // Adicionado Mission
import { createMissionTemplate, updateMissionTemplate } from '@/lib/gamification/missionService'; // Ajuste o caminho
import { toast } from 'react-hot-toast';
import { Loader2, Save } from 'lucide-react';
import { Json } from '../../../types/supabase'; // Corrigido o caminho para o tipo Json, assumindo que types/supabase.ts está em src/

// Definir os tipos exatos para mission_type e frequency baseados no schema ou uso
// Por enquanto, usando string, mas idealmente seriam union types mais restritos.
const MISSION_TYPES = ['daily', 'weekly', 'event', 'achievement', 'custom'];
const MISSION_FREQUENCIES = ['once', 'daily', 'weekly', 'monthly', 'unlimited'];


interface MissionFormProps {
  initialData?: MissionRow | null;
  onSave: (mission: Mission) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

// Omitir campos que não devem ser editados diretamente ou são gerenciados pelo DB
type FormDataType = Partial<Omit<MissionRow, 'id' | 'created_at' | 'updated_at' | 'required_actions'>> & {
  required_actions_string?: string; // Campo para a string JSON
};

const MissionForm: React.FC<MissionFormProps> = ({ initialData, onSave, onCancel, isLoading }) => {
  const [formData, setFormData] = useState<FormDataType>({
    mission_key: '',
    title: '',
    description: '',
    mission_type: MISSION_TYPES[0],
    frequency: MISSION_FREQUENCIES[0],
    required_actions_string: '[]', // String JSON inicial
    xp_reward: 0,
    badge_reward_id: null,
    is_active: true,
    is_publicly_assignable: true,
    start_date: null,
    end_date: null,
  });
  const [formError, setFormError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (initialData) {
      setFormData({
        ...initialData,
        required_actions_string: JSON.stringify(initialData.required_actions || [], null, 2),
        badge_reward_id: initialData.badge_reward_id || null,
        start_date: initialData.start_date ? initialData.start_date.split('T')[0] : null,
        end_date: initialData.end_date ? initialData.end_date.split('T')[0] : null,
        is_publicly_assignable: initialData.is_publicly_assignable === undefined ? true : initialData.is_publicly_assignable,
      });
    } else {
      // Reset para valores padrão ao criar novo
      setFormData({
        mission_key: '',
        title: '',
        description: '',
        mission_type: MISSION_TYPES[0],
        frequency: MISSION_FREQUENCIES[0],
        required_actions_string: '[]',
        xp_reward: 0,
        badge_reward_id: null,
        is_active: true,
        is_publicly_assignable: true,
        start_date: null,
        end_date: null,
      });
    }
  }, [initialData]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      setFormData(prev => ({ ...prev, [name]: (e.target as HTMLInputElement).checked }));
    } else if (type === 'number') {
      setFormData(prev => ({ ...prev, [name]: parseInt(value, 10) || 0 }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleRequiredActionsStringChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setFormData(prev => ({ ...prev, required_actions_string: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    setIsSubmitting(true);

    let parsedRequiredActions: MissionRequiredActions;
    try {
      parsedRequiredActions = JSON.parse(formData.required_actions_string || '[]');
      if (!Array.isArray(parsedRequiredActions)) {
        throw new Error('O JSON de Ações Requeridas deve ser um array.');
      }
      for (const action of parsedRequiredActions) {
        const reqAction = action as MissionActionRequirement; // Cast para verificar
        if (typeof reqAction.action_type !== 'string' || typeof reqAction.quantity !== 'number') {
          throw new Error('Cada ação requerida deve ter "action_type" (string) e "quantity" (number).');
        }
      }
    } catch (error: any) {
      setFormError(`Erro no formato JSON das Ações Requeridas: ${error.message}`);
      setIsSubmitting(false);
      toast.error(`JSON inválido para Ações Requeridas: ${error.message}`);
      return;
    }

    // Preparar dados para submissão
    // Certifique-se de que todos os campos obrigatórios no banco de dados (exceto id, created_at, updated_at)
    // tenham um valor ou um default apropriado.
    const missionPayload: Omit<MissionRow, 'id' | 'created_at' | 'updated_at'> = {
      mission_key: formData.mission_key || '',
      title: formData.title || '',
      description: formData.description || '',
      mission_type: formData.mission_type || MISSION_TYPES[0],
      frequency: formData.frequency || MISSION_FREQUENCIES[0],
      required_actions: parsedRequiredActions as unknown as Json, // Cast para Json
      xp_reward: formData.xp_reward || 0,
      badge_reward_id: formData.badge_reward_id || null,
      is_active: formData.is_active === undefined ? true : formData.is_active,
      is_publicly_assignable: formData.is_publicly_assignable === undefined ? true : formData.is_publicly_assignable,
      // Formatar datas para ISO string se presentes, ou null
      start_date: formData.start_date ? new Date(formData.start_date).toISOString() : null,
      end_date: formData.end_date ? new Date(formData.end_date).toISOString() : null,
    };
    
    try {
      let savedMission: Mission | null = null;
      if (initialData?.id) {
        // Atualizar
        savedMission = await updateMissionTemplate(supabase, initialData.id, missionPayload);
      } else {
        // Criar
        savedMission = await createMissionTemplate(supabase, missionPayload);
      }

      if (savedMission) {
        toast.success(`Missão ${initialData?.id ? 'atualizada' : 'criada'} com sucesso!`);
        onSave(savedMission);
      } else {
        throw new Error(`Falha ao ${initialData?.id ? 'atualizar' : 'criar'} a missão.`);
      }
    } catch (error: any) {
      console.error('Erro ao salvar missão:', error);
      setFormError(error.message || 'Ocorreu um erro desconhecido.');
      toast.error(error.message || 'Falha ao salvar missão.');
    } finally {
      setIsSubmitting(false);
    }
  };


  return (
    <Card className="w-full max-w-2xl mx-auto">
      <CardHeader>
        <CardTitle>{initialData?.id ? 'Editar Missão' : 'Criar Nova Missão'}</CardTitle>
        <CardDescription>
          {initialData?.id ? 'Modifique os detalhes da missão existente.' : 'Preencha os detalhes para uma nova missão.'}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="mission_key">Chave da Missão (única)</Label>
              <Input id="mission_key" name="mission_key" value={formData.mission_key || ''} onChange={handleChange} required />
            </div>
            <div>
              <Label htmlFor="title">Título</Label>
              <Input id="title" name="title" value={formData.title || ''} onChange={handleChange} required />
            </div>
          </div>

          <div>
            <Label htmlFor="description">Descrição</Label>
            <Textarea id="description" name="description" value={formData.description || ''} onChange={handleChange} required />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="mission_type">Tipo de Missão</Label>
              <select 
                id="mission_type" 
                name="mission_type" 
                value={formData.mission_type || ''} 
                onChange={handleChange}
                className="w-full mt-1 p-2 border rounded-md bg-white dark:bg-gray-800 dark:border-gray-700"
                required
              >
                {MISSION_TYPES.map(type => <option key={type} value={type}>{type}</option>)}
              </select>
            </div>
            <div>
              <Label htmlFor="frequency">Frequência</Label>
              <select 
                id="frequency" 
                name="frequency" 
                value={formData.frequency || ''} 
                onChange={handleChange}
                className="w-full mt-1 p-2 border rounded-md bg-white dark:bg-gray-800 dark:border-gray-700"
                required
              >
                {MISSION_FREQUENCIES.map(freq => <option key={freq} value={freq}>{freq}</option>)}
              </select>
            </div>
          </div>
          
          <div>
            <Label htmlFor="required_actions_string">Ações Requeridas (JSON)</Label>
            <Textarea
              id="required_actions_string"
              name="required_actions_string"
              value={formData.required_actions_string || '[]'}
              onChange={handleRequiredActionsStringChange}
              rows={6}
              placeholder='[{"action_type": "create_post", "quantity": 1}, {"action_type": "rate_game", "quantity": 5, "params": {"category": "strategy"}}]'
              className="font-mono text-sm"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Insira um array JSON de objetos. Cada objeto deve ter "action_type" (string), "quantity" (number) e opcionalmente "params" (objeto).
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="xp_reward">Recompensa de XP</Label>
              <Input id="xp_reward" name="xp_reward" type="number" value={formData.xp_reward || 0} onChange={handleChange} />
            </div>
            <div>
              <Label htmlFor="badge_reward_id">ID da Badge de Recompensa (Opcional)</Label>
              <Input id="badge_reward_id" name="badge_reward_id" value={formData.badge_reward_id || ''} onChange={handleChange} placeholder="UUID da badge" />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="start_date">Data de Início (Opcional)</Label>
              <Input id="start_date" name="start_date" type="date" value={formData.start_date || ''} onChange={handleChange} />
            </div>
            <div>
              <Label htmlFor="end_date">Data de Fim (Opcional)</Label>
              <Input id="end_date" name="end_date" type="date" value={formData.end_date || ''} onChange={handleChange} />
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <Checkbox id="is_active" name="is_active" checked={formData.is_active || false} onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_active: !!checked }))} />
            <Label htmlFor="is_active" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
              Missão Ativa
            </Label>
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox id="is_publicly_assignable" name="is_publicly_assignable" checked={formData.is_publicly_assignable || false} onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_publicly_assignable: !!checked }))} />
            <Label htmlFor="is_publicly_assignable" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
              Atribuível Publicamente (usuário pode ativar sozinho)
            </Label>
          </div>

          {formError && <p className="text-sm text-red-600">{formError}</p>}

          <div className="flex justify-end space-x-3 pt-4">
            <Button type="button" variant="outline" onClick={onCancel} disabled={isSubmitting || isLoading}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isSubmitting || isLoading}>
              {isSubmitting || isLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
              {initialData?.id ? 'Salvar Alterações' : 'Criar Missão'}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

export default MissionForm; 